import React, { useEffect, useRef, useState } from "react";
import { useDebounced } from "../utils/hooks";

// A dropdown with a search box. loadOptions(query) returns a promise of [{ value, label, hint }]; it is asked again
// (debounced) as the person types, so long lists are searched on the server instead of loaded in full.
// selected is the chosen option ({ value, label, hint }) so the button can show it before any search.
export default function SearchSelect({ label, selected, onChange, loadOptions, placeholder = "Search…", emptyText = "Nothing found" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const debounced = useDebounced(query);
  const box = useRef(null);
  const input = useRef(null);

  // Ask for options when opened and as the search changes; an older answer never replaces a newer one
  useEffect(() => {
    if (!open) return undefined;
    let current = true;
    setLoading(true);
    loadOptions(debounced)
      .then((list) => { if (current) { setOptions(list); setActive(0); } })
      .catch(() => { if (current) setOptions([]); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [open, debounced, loadOptions]);

  // Close on a click outside
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => { if (open && input.current) input.current.focus(); }, [open]);

  const choose = (option) => {
    onChange(option);
    setOpen(false);
    setQuery("");
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, options.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (options[active]) choose(options[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="search-select" ref={box}>
      {label && <span className="search-select-label">{label}</span>}
      <button type="button" className={`search-select-button ${open ? "is-open" : ""}`} onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open}>
        <span className="search-select-value">
          <b>{selected ? selected.label : "Choose…"}</b>
          {selected && selected.hint && <small>{selected.hint}</small>}
        </span>
        <span className="search-select-caret" aria-hidden="true" />
      </button>
      {open && (
        <div className="search-select-menu">
          <div className="search-select-search">
            <span aria-hidden="true">⌕</span>
            <input ref={input} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onKeyDown} placeholder={placeholder} aria-label={placeholder} />
          </div>
          <ul role="listbox">
            {loading && !options.length && <li className="search-select-note">Searching…</li>}
            {!loading && !options.length && <li className="search-select-note">{emptyText}</li>}
            {options.map((option, i) => (
              <li key={option.value} role="option" aria-selected={selected && selected.value === option.value}
                className={`${i === active ? "is-active" : ""} ${selected && selected.value === option.value ? "is-selected" : ""}`}
                onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); choose(option); }}>
                <b>{option.label}</b>
                {option.hint && <small>{option.hint}</small>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
